import React, { useMemo } from 'react';

import { Trans, useTranslation } from '@modernloop/i18n';
import { createSxProps } from '@modernloop/shared/components';
import { formatDurationFromMinutes, getTZAbbr } from '@modernloop/shared/datetime';
import { List, ListItem, Typography } from '@mui/material';

import { CandidateAvailabilityOptions, DEFAULT_NUMBER_OF_DAYS } from 'src/store/slices/candidate-availability-options';

import { formatToTimeZone } from 'src/utils/dateUtils';

import { DAY_CALC_FROM_INTERVIEW_PLAN, DURATION_CALC_FROM_INTERVIEW_PLAN } from 'src/constants';

type CandidateAvailabilityOptionsLabelProps = {
  options: CandidateAvailabilityOptions;
  candidateTimezone?: string;
  roundedMinsPerDay?: number;
};

const useSxProps = () => {
  const sxProps = useMemo(
    () => ({
      list: {
        listStyle: 'disc',
        marginLeft: (theme) => theme.spacing(2),
        padding: 0,
      },

      listItem: {
        display: 'list-item',
        paddingBottom: 0.5,
        paddingLeft: 0,
        paddingTop: 0,
      },
    }),
    []
  );

  return createSxProps(sxProps);
};

const CandidateAvailabilityOptionsLabel = ({
  options,
  candidateTimezone,
  roundedMinsPerDay,
}: CandidateAvailabilityOptionsLabelProps): JSX.Element => {
  const { t } = useTranslation();
  const sxProps = useSxProps();
  const { timezone } = options;

  return (
    <List sx={sxProps.list}>
      <ListItem sx={sxProps.listItem}>
        <Typography color="text.secondary">
          {t('hiring-dashboard-candidates:candidates.availabilityOptionsLabel.provideAvailability')}
          {options.numberOfDays !== DEFAULT_NUMBER_OF_DAYS && options.numberOfDays !== DAY_CALC_FROM_INTERVIEW_PLAN && (
            <Trans
              i18nKey="hiring-dashboard-candidates:candidates.availabilityOptionsLabel.onAtLeastDays"
              count={options.numberOfDays}
              components={{ strong: <strong /> }}
            />
          )}
          <Trans
            i18nKey="hiring-dashboard-candidates:candidates.availabilityOptionsLabel.atLeastPerDay"
            values={{
              duration: `${
                options.minutesPerDays === DURATION_CALC_FROM_INTERVIEW_PLAN
                  ? t('hiring-dashboard-candidates:candidates.availabilityOptionsLabel.calculatedDuration')
                  : formatDurationFromMinutes(options.minutesPerDays)
              }${
                !roundedMinsPerDay ||
                options.minutesPerDays === roundedMinsPerDay ||
                options.minutesPerDays === DURATION_CALC_FROM_INTERVIEW_PLAN
                  ? ''
                  : '*'
              }`,
            }}
            components={{ strong: <strong /> }}
          />
          <Trans
            i18nKey="hiring-dashboard-candidates:candidates.availabilityOptionsLabel.blocksOfOrMore"
            values={{
              duration:
                options.minimumTimeBlockMinutes === DURATION_CALC_FROM_INTERVIEW_PLAN
                  ? t('hiring-dashboard-candidates:candidates.availabilityOptionsLabel.calculatedDuration')
                  : formatDurationFromMinutes(options.minimumTimeBlockMinutes),
            }}
            components={{ strong: <strong /> }}
          />
        </Typography>
      </ListItem>

      {(options.availabilityStartDate || options.availabilityEndDate) && candidateTimezone && (
        <ListItem sx={sxProps.listItem}>
          {/* Availability start & end date and advance notice */}
          <Typography color="text.secondary">
            {t('hiring-dashboard-candidates:candidates.availabilityOptionsLabel.availabilityNeedsToBe')}
            {options.availabilityStartDate && (
              <strong>
                {t('hiring-dashboard-candidates:candidates.availabilityOptionsLabel.onOrAfter', {
                  date: `${formatToTimeZone(options.availabilityStartDate, 'MMM d, yyyy', timezone)}${
                    candidateTimezone !== timezone ? ` ${getTZAbbr(timezone)}` : ''
                  }`,
                })}
              </strong>
            )}
            {options.availabilityStartDate &&
              options.availabilityEndDate &&
              t('hiring-dashboard-candidates:candidates.availabilityOptionsLabel.and')}
            {options.availabilityEndDate && (
              <strong>
                {t('hiring-dashboard-candidates:candidates.availabilityOptionsLabel.onOrBefore', {
                  date: `${formatToTimeZone(options.availabilityEndDate, 'MMM d, yyyy', timezone)}${
                    candidateTimezone !== timezone ? ` ${getTZAbbr(timezone)}` : ''
                  }`,
                })}
              </strong>
            )}
            {(options.availabilityStartDate || options.availabilityEndDate) && <>.</>}
          </Typography>
        </ListItem>
      )}

      {options.advanceNoticeMinutes !== undefined && options.advanceNoticeMinutes !== 0 && (
        <ListItem sx={sxProps.listItem}>
          <Typography color="text.secondary">
            <Trans
              i18nKey="hiring-dashboard-candidates:candidates.availabilityOptionsLabel.provideInAdvance"
              values={{ duration: formatDurationFromMinutes(options.advanceNoticeMinutes) }}
              components={{ strong: <strong /> }}
            />
          </Typography>
        </ListItem>
      )}
    </List>
  );
};

export default CandidateAvailabilityOptionsLabel;
